import { useAppConstantsContext } from '@ltvco/refresh-lib/ctx';
import { Box } from '@ltvco/refresh-lib/theme';
import { RecordVoiceOver } from '@mui/icons-material';

import { type OralArgumentResult } from '../../types';
import {
  CardContainer,
  ContentContainer,
  HeaderRow,
  CardIconContainer,
  CardTitle,
  CourtCitation,
  DetailsContainer,
  DetailRow,
  DetailLabel,
  DetailValue,
  ViewButton,
  formatDate,
  formatDuration,
} from '../shared';

export interface OralArgumentsResultCardProps {
  argumentItem: OralArgumentResult;
  isHovered?: boolean;
}

export const OralArgumentsResultCard = ({
  argumentItem,
  isHovered,
}: OralArgumentsResultCardProps) => {
  const { links } = useAppConstantsContext();
  const {
    caseName,
    court_citation_string,
    dateArgued,
    duration,
    docketNumber,
    court,
    judge,
    snippet,
  } = argumentItem;

  const courtCitation =
    court_citation_string && dateArgued
      ? `(${court_citation_string} ${new Date(dateArgued).getFullYear()})`
      : '';

  const arguedDate = dateArgued ? formatDate(dateArgued) : '';
  const durationText = duration ? formatDuration(duration) : '';

  const handleViewClick = () => {
    window.open(
      `${links.baseUrl}/dashboard/court-cases/oral-arguments/${argumentItem.id}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  return (
    <CardContainer isHovered={isHovered} hoverColor="grey100">
      <ContentContainer>
        <HeaderRow>
          <CardIconContainer size={24}>
            <RecordVoiceOver />
          </CardIconContainer>
          <CardTitle>{caseName}</CardTitle>
          {courtCitation && <CourtCitation>{courtCitation}</CourtCitation>}
        </HeaderRow>

        <DetailsContainer>
          {arguedDate && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Argued:
              </DetailLabel>
              <DetailValue>{arguedDate}</DetailValue>
            </DetailRow>
          )}

          {durationText && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Duration:
              </DetailLabel>
              <DetailValue>{durationText}</DetailValue>
            </DetailRow>
          )}

          {docketNumber && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Docket:
              </DetailLabel>
              <DetailValue>{docketNumber}</DetailValue>
            </DetailRow>
          )}

          {court && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Court:
              </DetailLabel>
              <DetailValue>{court}</DetailValue>
            </DetailRow>
          )}

          {judge && (
            <DetailRow>
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Judge:
              </DetailLabel>
              <DetailValue>{judge}</DetailValue>
            </DetailRow>
          )}

          {snippet && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                alignItems: 'flex-start',
                width: '100%',
              }}
            >
              <DetailLabel minWidth="110px" mobileMinWidth="70px">
                Transcript:
              </DetailLabel>
              <DetailValue sx={{ textAlign: 'left', width: '100%' }}>
                {snippet}
              </DetailValue>
            </Box>
          )}
        </DetailsContainer>
      </ContentContainer>

      <ViewButton variant="outlined" onClick={handleViewClick}>
        View
      </ViewButton>
    </CardContainer>
  );
};
